<template>
  <div class="container grade-level-management-container">
    <el-card shadow="hover">
      <template #header>
        <div class="card-header">
          <h2>إدارة المستويات الدراسية</h2>
          <el-button type="primary" @click="handleCreate">
            <el-icon><Plus /></el-icon> إضافة مستوى جديد
          </el-button>
        </div>
      </template>

      <!-- شرح للواجهة -->
      <el-alert
        title="ما هو المستوى الدراسي؟"
        type="info"
        description="المستوى الدراسي هو تصنيف عام يجمع تحتها مجموعة من الصفوف (مثال: المرحلة الابتدائية، المرحلة الإعدادية، المرحلة الثانوية). هذا التصنيف يساعد في تنظيم السياسات الأكاديمية وعملية ترفيع الطلاب في نهاية العام."
        :closable="false"
        show-icon
        style="margin-bottom: 20px;"
      />

      <!-- جدول عرض المستويات الدراسية -->
      <el-table :data="gradeLevels" v-loading="loading" border style="width: 100%">
        <el-table-column prop="name" label="اسم المستوى" />
        <el-table-column prop="description" label="الوصف" />
        <el-table-column prop="classes_count" label="عدد الفصول" width="120" />
        <el-table-column label="الإجراءات" width="150" fixed="right">
          <template #default="{ row }">
            <el-button size="small" @click="handleEdit(row)">تعديل</el-button>
            <el-popconfirm title="هل أنت متأكد من حذف هذا المستوى؟" @confirm="handleDelete(row.id)">
              <template #reference><el-button size="small" type="danger">حذف</el-button></template>
            </el-popconfirm>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <!-- Dialog لإنشاء/تعديل المستوى -->
    <el-dialog v-model="dialogVisible" :title="dialogTitle" width="40%">
      <el-form :model="form" ref="formRef" label-position="top" :rules="rules">
        <el-form-item label="اسم المستوى الدراسي" prop="name">
          <el-input v-model="form.name" placeholder="مثال: المرحلة الابتدائية" />
        </el-form-item>
        <el-form-item label="الوصف" prop="description">
          <el-input v-model="form.description" type="textarea" placeholder="وصف موجز للمستوى (اختياري)" />
        </el-form-item>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="dialogVisible = false">إلغاء</el-button>
          <el-button type="primary" @click="submitForm" :loading="formLoading">حفظ</el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import axios from 'axios';
import { ElMessage, ElPopconfirm } from 'element-plus';
import { Plus } from '@element-plus/icons-vue';

export default {
  name: 'GradeLevelManagement',
  components: { Plus, ElPopconfirm },
  data() {
    return {
      api: null,
      loading: true,
      gradeLevels: [],
      dialogVisible: false,
      formLoading: false,
      form: this.getInitialForm(),
      rules: {
        name: [{ required: true, message: 'اسم المستوى مطلوب', trigger: 'blur' }],
      },
    };
  },
  computed: {
    dialogTitle() {
      return this.form.id ? 'تعديل المستوى الدراسي' : 'إضافة مستوى دراسي جديد';
    },
  },
  created() {
    this.api = axios.create({ baseURL: process.env.VUE_APP_API_BASE_URL || 'https://cale.caledonian.ly/api' });
    this.api.interceptors.request.use((config) => {
      const token = localStorage.getItem('access_token');
      if (token) config.headers['Authorization'] = `Bearer ${token}`;
      return config;
    });
    this.fetchGradeLevels();
  },
  methods: {
    getInitialForm() {
      return { id: null, name: '', description: '' };
    },
    async fetchGradeLevels() {
      this.loading = true;
      try {
        const response = await this.api.get('/grade-levels');
        this.gradeLevels = response.data.data || response.data;
      } catch (error) {
        ElMessage.error('فشل في جلب المستويات الدراسية.');
      } finally {
        this.loading = false;
      }
    },
    handleCreate() {
      this.form = this.getInitialForm();
      this.dialogVisible = true;
    },
    handleEdit(level) {
      this.form = { ...level };
      this.dialogVisible = true;
    },
    async submitForm() {
      if (!this.$refs.formRef) return;
      await this.$refs.formRef.validate(async (valid) => {
        if (!valid) return;
        this.formLoading = true;
        try {
          if (this.form.id) {
            await this.api.put(`/grade-levels/${this.form.id}`, this.form);
            ElMessage.success('تم تحديث المستوى بنجاح!');
          } else {
            await this.api.post('/grade-levels', this.form);
            ElMessage.success('تم إنشاء المستوى بنجاح!');
          }
          this.dialogVisible = false;
          this.fetchGradeLevels();
        } catch (error) {
          const errorMsg = error.response?.data?.errors ? Object.values(error.response.data.errors)[0][0] : (error.response?.data?.message || 'فشلت العملية.');
          ElMessage.error(errorMsg);
        } finally {
          this.formLoading = false;
        }
      });
    },
    async handleDelete(id) {
      try {
        await this.api.delete(`/grade-levels/${id}`);
        ElMessage.success('تم حذف المستوى بنجاح!');
        this.fetchGradeLevels();
      } catch (error) {
        ElMessage.error(error.response?.data?.message || 'فشل في حذف المستوى.');
      }
    },
  },
};
</script>

<style scoped>
.grade-level-management-container { padding: 20px; }
.card-header { display: flex; justify-content: space-between; align-items: center; }
</style>
